'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { format, startOfToday } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const DAY_NAME_MAP: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

interface DatePickerProps {
  nonAvailabilityDates?: string[];
  nonAvailabilityDays?: string[];
  weekendDays?: string[];
  specialDates?: string[];
  /** When false, only dates in availableDates are selectable. */
  defaultAvailability?: boolean;
  availableDates?: string[];
  className?: string;
  value?: Date;
  onDateSelect?: (
    date: Date,
    status: 'special' | 'weekend' | 'allowed'
  ) => void;
  disabled?: boolean;
}

export function DatePicker({
  className,
  nonAvailabilityDates = [],
  nonAvailabilityDays = [],
  weekendDays = [],
  specialDates = [],
  defaultAvailability = true,
  availableDates = [],
  value,
  onDateSelect,
  disabled = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const normalizeDay = (day: string) => day.toLowerCase().trim();

  const parseYYYYMMDD = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };

  const nonAvailabilityDatesStr = JSON.stringify(nonAvailabilityDates);
  const nonAvailableDateObjs = React.useMemo(
    () => nonAvailabilityDates.map(parseYYYYMMDD),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonAvailabilityDatesStr]
  );

  const availableDatesStr = JSON.stringify(availableDates);
  const availableDateObjs = React.useMemo(
    () => availableDates.map(parseYYYYMMDD),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableDatesStr]
  );

  const specialDatesStr = JSON.stringify(specialDates);
  const specialDateObjs = React.useMemo(
    () => specialDates.map(parseYYYYMMDD),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [specialDatesStr]
  );

  const nonAvailabilityDaysStr = JSON.stringify(nonAvailabilityDays);
  const disabledDaysSet = React.useMemo(
    () => new Set(nonAvailabilityDays.map(normalizeDay)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nonAvailabilityDaysStr]
  );

  const weekendDaysStr = JSON.stringify(weekendDays);
  const weekendDaySet = React.useMemo(
    () => new Set(weekendDays.map(normalizeDay)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekendDaysStr]
  );

  const isSpecial = React.useCallback(
    (day: Date) => {
      const normalizedDay = Date.UTC(
        day.getFullYear(),
        day.getMonth(),
        day.getDate()
      );
      return specialDateObjs.some(d => d.getTime() === normalizedDay);
    },
    [specialDateObjs]
  );

  const isWeekend = React.useCallback(
    (day: Date) => {
      const dayName = DAY_NAME_MAP[day.getDay()];
      return weekendDaySet.has(dayName);
    },
    [weekendDaySet]
  );

  const isDisabled = React.useCallback(
    (day: Date) => {
      const normalizedDay = Date.UTC(
        day.getFullYear(),
        day.getMonth(),
        day.getDate()
      );

      if (!defaultAvailability) {
        return !availableDateObjs.some(d => d.getTime() === normalizedDay);
      }

      const dayName = DAY_NAME_MAP[day.getDay()];

      return (
        disabledDaysSet.has(dayName) ||
        nonAvailableDateObjs.some(d => d.getTime() === normalizedDay)
      );
    },
    [
      defaultAvailability,
      availableDateObjs,
      disabledDaysSet,
      nonAvailableDateObjs,
    ]
  );

  const handleSelect = React.useCallback(
    (selected: Date | undefined) => {
      if (!selected) return;

      setOpen(false);
      setMobileOpen(false);

      if (isSpecial(selected)) {
        onDateSelect?.(selected, 'special');
      } else if (isWeekend(selected)) {
        onDateSelect?.(selected, 'weekend');
      } else {
        onDateSelect?.(selected, 'allowed');
      }
    },
    [isSpecial, isWeekend, onDateSelect]
  );

  return (
    <div className={cn('grid gap-2', className)}>
      {/* Desktop */}
      <div className='hidden md:block'>
        <Popover
          open={disabled ? false : open}
          onOpenChange={open => !disabled && setOpen(open)}
        >
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='outline'
              className={cn(
                'w-full justify-start text-left font-normal rounded-sm text-sm',
                !value && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className='mr-2 h-4 w-4 flex-shrink-0' />
              {value ? (
                <span className='truncate'>
                  {format(value, 'EEE dd MMM, yyyy')}
                </span>
              ) : (
                <span>Pick a date</span>
              )}
            </Button>
          </PopoverTrigger>

          <PopoverContent className='w-auto p-0' align='start'>
            <Calendar
              mode='single'
              selected={value}
              onSelect={handleSelect}
              disabled={isDisabled}
              fromDate={startOfToday()}
              className='w-full [&_.rdp-head]:justify-center [&_.rdp-head_cell]:text-center [&_.rdp-table]:mx-auto [&_.rdp-row]:justify-center [&_.rdp-cell]:text-center [&_.rdp-day]:mx-auto'
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Mobile */}
      <div className='md:hidden'>
        <Button
          type='button'
          variant='outline'
          className={cn(
            'w-full justify-start text-left font-normal rounded-sm text-sm',
            !value && 'text-muted-foreground'
          )}
          onClick={() => setMobileOpen(true)}
        >
          <CalendarIcon className='mr-2 h-4 w-4 flex-shrink-0' />
          {value ? (
            <span className='truncate'>
              {format(value, 'EEE dd MMM, yyyy')}
            </span>
          ) : (
            <span>Pick a date</span>
          )}
        </Button>

        {mobileOpen &&
          typeof document !== 'undefined' &&
          createPortal(
            <div className='fixed inset-0 z-[99999]'>
              <div
                className='absolute inset-0 bg-black/50'
                onClick={() => setMobileOpen(false)}
                aria-hidden
              />

              <div className='absolute bottom-0 left-0 right-0 z-[100000] bg-white rounded-t-2xl shadow-lg'>
                <div className='flex justify-center pt-3 pb-2'>
                  <div className='w-12 h-1 bg-gray-300 rounded-full' />
                </div>

                <div className='px-4 pb-3 border-b'>
                  <h3 className='text-lg font-semibold text-center'>
                    Select Date
                  </h3>
                </div>

                <div className='px-4 py-4 max-h-[60vh] overflow-y-auto flex justify-center'>
                  <Calendar
                    mode='single'
                    selected={value}
                    onSelect={handleSelect}
                    disabled={isDisabled}
                    fromDate={startOfToday()}
                    className='w-full [&_.rdp-head]:justify-center [&_.rdp-head_cell]:text-center [&_.rdp-table]:mx-auto [&_.rdp-row]:justify-center [&_.rdp-cell]:text-center [&_.rdp-day]:mx-auto'
                  />
                </div>

                <div className='px-4 pb-6 border-t'>
                  <Button
                    variant='outline'
                    className='w-full'
                    onClick={() => setMobileOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}
